import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { VERTICALS } from "@/data/verticals";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";

const NAV_LINKS = [
  { label: "How it works", to: "/how-it-works" },
  { label: "Performance", to: "/performance" },
  { label: "Case studies", to: "/case-studies" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 w-full border-b border-citirex-border bg-citirex-bg/85 backdrop-blur"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:h-20 md:px-12">
        <Link
          to="/"
          data-testid="nav-logo"
          className="group flex items-center gap-2 font-display text-2xl font-semibold tracking-tight text-citirex-ink"
        >
          <span className="inline-block h-3 w-3 bg-citirex-accent transition-transform group-hover:rotate-45" />
          CITIREX
        </Link>

        <div className="hidden lg:flex">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  data-testid="nav-verticals-trigger"
                  className="bg-transparent font-body text-sm font-medium tracking-tight text-citirex-ink hover:bg-transparent hover:text-citirex-accent data-[state=open]:bg-transparent data-[state=open]:text-citirex-accent"
                >
                  Verticals
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid w-[640px] grid-cols-2 gap-0 bg-citirex-surface p-2">
                    {VERTICALS.map((v) => (
                      <NavigationMenuLink asChild key={v.slug}>
                        <Link
                          to={`/verticals/${v.slug}`}
                          data-testid={`nav-vertical-${v.slug}`}
                          className="group flex items-start justify-between gap-4 border border-transparent p-4 transition-colors hover:border-citirex-border hover:bg-citirex-bg2"
                        >
                          <div>
                            <p className="font-editorial text-xs text-citirex-sub">
                              {v.index}
                            </p>
                            <p className="mt-1 font-display text-lg font-medium text-citirex-ink">
                              {v.name}
                            </p>
                            <p className="mt-1 max-w-[220px] text-xs leading-snug text-citirex-sub">
                              {v.tagline}
                            </p>
                          </div>
                          <ArrowUpRight
                            size={18}
                            weight="bold"
                            className="mt-1 shrink-0 -translate-x-1 translate-y-1 text-citirex-ink opacity-0 transition-all group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100"
                          />
                        </Link>
                      </NavigationMenuLink>
                    ))}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {NAV_LINKS.map((l) => (
                <NavigationMenuItem key={l.to}>
                  <NavigationMenuLink asChild>
                    <NavLink
                      to={l.to}
                      data-testid={`nav-link-${l.to.replace("/", "")}`}
                      className={({ isActive }) =>
                        `inline-flex h-9 items-center px-4 font-body text-sm font-medium tracking-tight transition-colors ${
                          isActive
                            ? "text-citirex-accent"
                            : "text-citirex-ink hover:text-citirex-accent"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/contact"
            data-testid="nav-cta-primary"
            className="group inline-flex items-center gap-2 bg-citirex-ink px-5 py-3 font-body text-sm font-medium text-citirex-inv transition-colors hover:bg-citirex-accent"
          >
            Talk to sales
            <ArrowUpRight
              size={16}
              weight="bold"
              className="transition-transform group-hover:rotate-45"
            />
          </Link>
        </div>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle navigation"
          onClick={() => setMobileOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center border border-citirex-border bg-citirex-surface text-citirex-ink lg:hidden"
        >
          {mobileOpen ? <X size={20} /> : <List size={20} />}
        </button>
      </div>

      {mobileOpen && (
        <div
          data-testid="nav-mobile-panel"
          className="border-t border-citirex-border bg-citirex-bg lg:hidden"
        >
          <div className="mx-auto max-w-7xl px-6 py-6">
            <p className="overline mb-3">Verticals</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {VERTICALS.map((v) => (
                <Link
                  key={v.slug}
                  to={`/verticals/${v.slug}`}
                  data-testid={`nav-mobile-vertical-${v.slug}`}
                  className="flex items-center justify-between border border-citirex-border bg-citirex-surface px-4 py-3 font-body text-sm text-citirex-ink"
                >
                  <span>
                    <span className="mr-2 font-editorial text-xs text-citirex-sub">
                      {v.index}
                    </span>
                    {v.name}
                  </span>
                  <ArrowUpRight size={14} />
                </Link>
              ))}
            </div>
            <p className="overline mb-3 mt-6">Company</p>
            <div className="flex flex-col gap-2">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.to.replace("/", "")}`}
                  className="border border-citirex-border bg-citirex-surface px-4 py-3 font-body text-sm text-citirex-ink"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="nav-mobile-cta"
                className="mt-2 inline-flex items-center justify-center bg-citirex-ink px-4 py-3 font-body text-sm font-medium text-citirex-inv"
              >
                Talk to sales
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
